import React, { useState, useEffect } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '@mes-amis/galette-design-system';
import { getStorageQuota, formatBytes } from '../../lib/storage-utils';
import type { StorageQuotaInfo } from '../../types/storage';

/**
 * Storage Tab - Shows storage usage breakdown by client
 */
export default function StorageTab() {
  const [isLoading, setIsLoading] = useState(true);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<StorageQuotaInfo | null>(null);
  const [lastCalculated, setLastCalculated] = useState<Date | null>(null);

  const calculateStorage = async () => {
    try {
      setIsCalculating(true);
      setError(null);

      const quotaInfo = await getStorageQuota();
      setQuota(quotaInfo);

      setLastCalculated(new Date());
    } catch (err) {
      console.error('Error calculating storage:', err);
      setError(err instanceof Error ? err.message : 'Failed to calculate storage');
    } finally {
      setIsLoading(false);
      setIsCalculating(false);
    }
  };

  useEffect(() => {
    calculateStorage();
  }, []);

  const percentUsed = quota && quota.quota > 0
    ? Math.round((quota.usage / quota.quota) * 100)
    : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
          <p className="mt-2 text-sm text-gray-600">Calculating storage...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <ExclamationCircleIcon className="w-6 h-6 text-red-600 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error calculating storage</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
            <Button variant="primary" destructive size="sm" onClick={calculateStorage} className="mt-3">
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Total Storage Overview */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">
          Storage Usage Overview
        </h2>

        {quota ? (
          <div className="space-y-4">
            <div className="flex justify-between items-baseline">
              <span className="text-2xl font-bold text-gray-900">
                {formatBytes(quota.usage)}
              </span>
              <span className="text-sm text-gray-600">
                of {formatBytes(quota.quota)} available
              </span>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-deep-sea-green h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(percentUsed, 100)}%` }}
              />
            </div>

            <p className="text-sm text-gray-600">
              {percentUsed}% of available storage used
            </p>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <p className="text-sm text-yellow-800">
              Storage quota information not available in this browser. Storage breakdown by client is still shown below.
            </p>
          </div>
        )}

        {/* Recalculate Button */}
        <div className="mt-6 flex items-center gap-3">
          <Button variant="primary" onClick={calculateStorage} disabled={isCalculating}>
            {isCalculating ? 'Recalculating...' : 'Recalculate Storage'}
          </Button>
          {lastCalculated && !isCalculating && (
            <span className="text-sm text-gray-600">
              Last calculated: {lastCalculated.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

    </div>
  );
}
